import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, TrendingDown, Minus, Info } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { supabase } from "@/integrations/supabase/client";

interface FearGreedData {
  value: number;
  label: string;
}

export const FearGreedIndex = () => {
  const [data, setData] = useState<FearGreedData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchFearGreedData();
    const interval = setInterval(fetchFearGreedData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchFearGreedData = async () => {
    try {
      const { data: response, error: fnError } = await supabase.functions.invoke('fetch-market-overview');
      if (fnError) throw fnError;

      const fgValue = response?.fearGreedIndex ?? response?.fear_greed_index;
      const fgLabel = response?.fearGreedLabel ?? response?.fear_greed_label ?? getLabel(fgValue);

      if (fgValue != null) {
        setData({ value: Number(fgValue), label: fgLabel });
        setError(null);
      } else {
        throw new Error('Fear & Greed data not available');
      }
    } catch (err) {
      console.error('Error fetching Fear & Greed Index:', err);
      setError('Unable to load index data');
    } finally {
      setLoading(false);
    }
  };

  const getLabel = (v: number) => {
    if (v <= 25) return 'Extreme Fear';
    if (v <= 45) return 'Fear';
    if (v <= 55) return 'Neutral';
    if (v <= 75) return 'Greed';
    return 'Extreme Greed';
  };

  const getColorClass = (value: number) => {
    if (value <= 25) return 'text-destructive';
    if (value <= 45) return 'text-warning';
    if (value <= 55) return 'text-muted-foreground';
    return 'text-success';
  };

  const getBackgroundGradient = (value: number) => {
    if (value <= 25) return 'from-destructive/20 to-destructive/5';
    if (value <= 45) return 'from-warning/20 to-warning/5';
    if (value <= 55) return 'from-muted/20 to-muted/5';
    if (value <= 75) return 'from-success/20 to-success/5';
    return 'from-success/30 to-success/5';
  };

  const getIcon = (label: string) => {
    const lower = label.toLowerCase();
    if (lower.includes('fear')) return <TrendingDown className="w-5 h-5" />;
    if (lower.includes('greed')) return <TrendingUp className="w-5 h-5" />;
    return <Minus className="w-5 h-5" />;
  };

  const getSentimentBadge = (label: string) => {
    if (!label) return <Badge variant="secondary">Unknown</Badge>;
    const lower = label.toLowerCase();
    if (lower.includes('extreme fear')) return <Badge variant="destructive">Extreme Fear</Badge>;
    if (lower.includes('fear')) return <Badge variant="secondary" className="bg-warning/20 text-warning border-warning/30">Fear</Badge>;
    if (lower.includes('neutral')) return <Badge variant="secondary">Neutral</Badge>;
    if (lower.includes('extreme greed')) return <Badge className="bg-success text-success-foreground">Extreme Greed</Badge>;
    if (lower.includes('greed')) return <Badge variant="secondary" className="bg-success/20 text-success border-success/30">Greed</Badge>;
    return <Badge variant="secondary">{label}</Badge>;
  };

  if (loading) {
    return (
      <Card className="bg-card/50 backdrop-blur border-border/50">
        <CardContent className="p-6">
          <Skeleton className="h-8 w-48 mb-4" />
          <Skeleton className="h-32 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card className="bg-card/50 backdrop-blur border-border/50">
        <CardContent className="p-6">
          <p className="text-muted-foreground">{error || 'No data available'}</p>
        </CardContent>
      </Card>
    );
  }

  const { value, label } = data;
  const gaugeRotation = (value / 100) * 180 - 90;

  return (
    <Card className="bg-card/50 backdrop-blur border-border/50">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold text-foreground">Crypto Fear & Greed Index</h2>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Info className="w-4 h-4 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="text-sm">
                    The Fear & Greed Index analyzes emotions and sentiments from different sources and crunches them into one simple number.
                    0-24: Extreme Fear | 25-44: Fear | 45-55: Neutral | 56-75: Greed | 76-100: Extreme Greed
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          {getSentimentBadge(label)}
        </div>

        <div className={`relative bg-gradient-to-br ${getBackgroundGradient(value)} rounded-lg p-8`}>
          <div className="flex flex-col items-center">
            <div className="relative w-64 h-32 mb-4">
              <svg viewBox="0 0 200 100" className="w-full h-full">
                <path d="M 10 90 A 90 90 0 0 1 190 90" fill="none" stroke="currentColor" strokeWidth="12" className="text-muted/20" />
                <path d="M 10 90 A 90 90 0 0 1 190 90" fill="none" stroke="currentColor" strokeWidth="12" strokeDasharray={`${(value / 100) * 283} 283`} className={getColorClass(value)} />
                <line x1="100" y1="90" x2="100" y2="20" stroke="currentColor" strokeWidth="3" strokeLinecap="round" className={getColorClass(value)} style={{ transform: `rotate(${gaugeRotation}deg)`, transformOrigin: '100px 90px', transition: 'transform 0.5s ease-out' }} />
                <circle cx="100" cy="90" r="5" fill="currentColor" className={getColorClass(value)} />
              </svg>
            </div>

            <div className="text-center">
              <div className={`text-6xl font-bold ${getColorClass(value)} mb-2`}>{value}</div>
              <div className="flex items-center gap-2 text-lg text-foreground mb-2">
                {getIcon(label)}
                <span className="font-semibold">{label}</span>
              </div>
            </div>

            <div className="flex justify-between w-full max-w-md mt-6 text-xs text-muted-foreground">
              <div className="text-center">
                <div className="font-semibold text-destructive">0</div>
                <div>Extreme Fear</div>
              </div>
              <div className="text-center">
                <div className="font-semibold">50</div>
                <div>Neutral</div>
              </div>
              <div className="text-center">
                <div className="font-semibold text-success">100</div>
                <div>Extreme Greed</div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
          <p className="text-sm text-muted-foreground mb-3">
            <span className="font-semibold text-foreground">What is this?</span> The Fear & Greed Index measures overall crypto market sentiment from 0 (Extreme Fear) to 100 (Extreme Greed), based on volatility, volume, social media, and market momentum.
          </p>
          <p className="text-sm text-muted-foreground mb-3">
            <span className="font-semibold text-foreground">How to use it:</span> Use the index as a contrarian signal — "Be fearful when others are greedy, and greedy when others are fearful." Extreme Fear (0-25) often signals potential buying opportunities as assets may be oversold. Extreme Greed (75-100) suggests caution, as the market may be overbought.
          </p>
        </div>

        <div className="mt-4 p-4 bg-muted/30 rounded-lg">
          <h3 className="font-semibold text-foreground mb-2">Current Market Reading</h3>
          <p className="text-sm text-muted-foreground">
            {value <= 25 && "🔴 Extreme fear may indicate oversold conditions and potential buying opportunities. However, it can also signal legitimate concerns in the market."}
            {value > 25 && value <= 45 && "🟠 Fear in the market suggests investors are worried. This could be a time to look for value, but proceed with caution."}
            {value > 45 && value <= 55 && "⚪ The market is in a neutral state. Investors are neither particularly fearful nor greedy."}
            {value > 55 && value <= 75 && "🟢 Greed is building in the market. Investors are becoming optimistic, which may indicate overbought conditions approaching."}
            {value > 75 && "🔥 Extreme greed suggests the market may be overheated. This could be a signal to take profits or be more cautious with new positions."}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};
